import { inject, injectable } from "inversify";
import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { webScriptBackend } from "./webScriptBackend";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { IMUserStore } from "@whatslly/instant-messaging/IMUserStore";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { backgroundClient } from "@whatslly/commons/background/client";
import type { EmittedMessage, IMessageEmitter } from "@whatslly/types/instant-messaging/messagesEmitter";
import { AllChats, InjectMessageEmitter } from "@whatslly/types/instant-messaging/messagesEmitter";
import { contentBackend } from "@whatslly/commons/contentBackend";
import { ContentCommands } from "@whatslly/commons/contentCommands";
import type { SurveillanceSettings } from "@whatslly/types/surveillance/SurveillancePolicy";
import { Direction } from "@whatslly/types/policy/enums";
import type { ScanSurveillanceBody } from "@whatslly/types/integrations/Surveillance";
import { reaction } from "mobx";

export const InjectSurveillance = Symbol("InjectSurveillance");
export interface ISurveillance {
  init(selectors: WhatsappSelectorsType): Promise<void>;
}

@injectable()
export class MessageSurveillanceService implements ISurveillance {
  protected selectors?: WhatsappSelectorsType;
  protected logger: FrontLogger;
  #surveillanceSettings: SurveillanceSettings | null = null;
  private abortController: AbortController | undefined;

  constructor(
    @inject(FrontLogger) frontLogger: FrontLogger,
    @inject(InjectStorage) protected readonly storage: IStorage,
    @inject(IMUserStore) protected readonly imUserStore: IMUserStore,
    @inject(InjectMessageEmitter) private readonly MessageEmitter: IMessageEmitter<any, any>
  ) {
    this.logger = frontLogger.getLogger("message-surveillance-service");
  }

  reset() {
    this.#surveillanceSettings = null;
    this.abortController?.abort();
  }

  private get hasPolicies() {
    return Boolean(this.#surveillanceSettings?.policies?.length);
  }

  private async scanMessage(message: EmittedMessage) {
    if (!this.hasPolicies) return;

    const direction = message.isSentByMe ? Direction.OUTBOUND : Direction.INBOUND;
    const payload: ScanSurveillanceBody = {
      direction,
      content: message.text ?? "",
      sender: message.senderName ?? "-",
      senderPhone: message.senderPhone ?? "-",
      recipient: message.receiverName ?? "",
      recipientPhone: message.receiverPhone ?? "",
      isGroup: message.isGroupMsg ?? false,
      source: this.getSource(),
    };

    try {
      await backgroundClient.send(BgCommands.HTTP_REQUEST, {
        urlSuffix: `/integrations/surveillance/scan`,
        method: "POST",
        body: JSON.stringify(payload),
      });
    } catch (err) {
      this.logger.warn({ err }, "Surveillance scan failed (fire-and-forget)");
    }
  }

  private getSource(): "desktop" | "extension" {
    if (typeof process !== "undefined") return "desktop";
    return "extension";
  }

  private async scrapeMessages() {
    if (!this.imUserStore.user) return;
    this.abortController?.abort();
    this.abortController = new AbortController();
    const scraper = new this.MessageEmitter(
      AllChats,
      {
        ignoreOldMessages: true,
        listenToNewMessages: true,
        scrollToEarliestMessage: false,
        abortSignal: this.abortController.signal,
      },
      this.logger
    );
    for await (const message of scraper) {
      this.scanMessage(message);
    }
  }

  private async loadSettings() {
    this.#surveillanceSettings = await this.storage.get(STORAGE.SURVEILLANCE_SETTINGS);
  }

  get isSurveillanceEnabled(): boolean {
    return Boolean(this.imUserStore.user?.enable_surveillance);
  }

  async init(selectors: WhatsappSelectorsType) {
    this.selectors = selectors;
    this.MessageEmitter.inject(selectors);
    webScriptBackend
      .on(WebScriptCommands.LOGOUT, () => this.reset())
      .on([WebScriptCommands.USER_REFRESHED, WebScriptCommands.AUTH], async () => {
        if (!this.isSurveillanceEnabled) {
          this.abortController?.abort();
          return;
        }
        await this.loadSettings();
        this.scrapeMessages();
      })
      .on(WebScriptCommands.SURVEILLANCE_POLICIES_CHANGED, async () => {
        if (!this.isSurveillanceEnabled) {
          this.abortController?.abort();
          return;
        }
        await this.loadSettings();
        this.scrapeMessages();
      });
    contentBackend.on(ContentCommands.CONTENT_READY, async () => {
      this.MessageEmitter.inject(selectors);
    });

    reaction(
      () => this.isSurveillanceEnabled,
      async (value) => {
        if (value) {
          await this.loadSettings();
          this.scrapeMessages();
          return;
        }
        this.abortController?.abort();
      }
    );
  }
}
